import {
  Auth,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  getMultiFactorResolver,
  getRedirectResult,
  GoogleAuthProvider,
  multiFactor,
  MultiFactorResolver,
  OAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reload,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
} from "firebase/auth";
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { env } from "../env";
import { requestVerificationEmailAuthRequestVerificationEmailPost } from "../api/openapi/organization/sdk.gen";
import { reportHandledError } from "../monitoring";
import {
  addOrganizationListener,
  clearCurrentOrganization,
  getCurrentOrganizationId,
  setCurrentOrganizationId,
} from "../organization/OrganizationService";
import {
  applySignedOutWhileChallengePending,
  cancelChallengePatch,
  classifyAuthStateTransition,
  classifyRedirectFailure,
  expiredChallengeAuthPatch,
  formatRedirectFatalError,
  interpretSignInError,
  isMultiFactorAuthRequiredError,
  mapMfaResolveError,
  type MfaChallengeErrorKind,
  pendingChallengeAuthPatch,
  resolvedChallengeAuthPatch,
} from "./mfaChallenge";
import {
  getStoredDeepLink,
  hasReachedDeepLink,
  setStoredDeepLink,
  signOutPreservingDeepLink,
  toAuthReturnPath,
} from "./deepLink";
import {
  getReauthenticationProviderIds,
  getTotpEnrollmentFactors,
  mapMfaEnrollmentError,
  MfaEnrollmentError,
  MfaEnrollmentFactor,
  PASSWORD_PROVIDER_ID,
} from "./mfaEnrollment";
import { isPopupCancelledError, shouldUseOAuthPopup } from "./oauthPopupFlow";
import { membershipsRequireMfa } from "./mfaCompliance";
import { ApiLoadingState, ApiNotFoundError, AuthenticatePermissions, AuthResult, UserStatus } from "./types";

type AuthState = {
  user: User | null;
  isEmailVerified: boolean;
  loading: ApiLoadingState;
  permissions: {
    id?: number;
    organizations: any[];
    currentOrganizationId?: number;
    status?: UserStatus;
    superadmin?: boolean;
    superadmin_mfa_required?: boolean;
  } | null;
  isProcessingDeepLink: boolean;
  deepLink: string | null;
  /** Error from redirect flow (e.g. getRedirectResult). Shown once on login form then cleared. */
  authError: string | null;
  /** Set when authError came from a failed MFA step, so the login page can translate it. */
  authErrorKind: MfaChallengeErrorKind | null;
  mfaChallengePending: boolean;
};

type AuthContextType = AuthState & {
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: (options?: { preserveDeepLink?: string }) => Promise<void>;
  refreshSession: (user: User) => Promise<AuthenticatePermissions>;
  /** Reloads the Firebase user; if email is verified, refreshes backend session. */
  syncEmailVerificationFromFirebase: () => Promise<void>;
  /** Sends verification email, or refreshes session and returns `alreadyVerified` if Firebase already shows verified. */
  resendEmailVerification: () => Promise<"sent" | "alreadyVerified">;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  setDeepLink: (url: string | null) => void;
  /** Clears stored and in-flight deep-link navigation state without signing out. */
  cancelDeepLinkNavigation: () => void;
  clearAuthError: () => void;
  completeMfaChallenge: (otpCode: string) => Promise<void>;
  cancelMfaChallenge: () => void;
  getMfaEnrollmentFactors: () => Promise<MfaEnrollmentFactor[]>;
  getReauthenticationProviderIds: () => string[];
  reauthenticateForSensitiveOperation: (providerId: string, password?: string) => Promise<{ mfaRequired: boolean }>;
  completeSensitiveReauthenticationMfa: (otpCode: string) => Promise<void>;
  beginTotpEnrollment: () => Promise<TotpSecret>;
  completeTotpEnrollment: (secret: TotpSecret, otpCode: string) => Promise<void>;
  removeMfaEnrollmentFactor: (factorUid: string) => Promise<void>;
};

const initialAuthState: AuthState = {
  user: null,
  isEmailVerified: false,
  loading: ApiLoadingState.loading,
  permissions: null,
  isProcessingDeepLink: false,
  deepLink: getStoredDeepLink(),
  authError: null,
  authErrorKind: null,
  mfaChallengePending: false,
};

const getOrganizationApiBaseUrl = (): string => {
  const baseUrl = env.VITE_ORGANIZATION_API_URL;
  if (!baseUrl) {
    throw new Error("VITE_ORGANIZATION_API_URL is not configured");
  }
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

async function postRequestVerificationEmail(firebaseIdToken: string, returnTo: string | undefined): Promise<void> {
  await requestVerificationEmailAuthRequestVerificationEmailPost({
    baseUrl: getOrganizationApiBaseUrl(),
    headers: { Authorization: `Bearer ${firebaseIdToken}`, "Content-Type": "application/json" },
    body: { return_to: returnTo },
    throwOnError: true,
  });
}

async function postRequestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${getOrganizationApiBaseUrl()}/auth/request-password-reset/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Password reset request failed (${response.status})`);
  }
}

function selectTotpHint(resolver: MultiFactorResolver) {
  return resolver.hints.find((hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? resolver.hints[0];
}

export const AuthContext = React.createContext<AuthContextType>({
  ...initialAuthState,
  login: async () => {},
  signUp: async () => {},
  loginWithGoogle: async () => {},
  loginWithMicrosoft: async () => {},
  logout: async () => {},
  refreshSession: async () => ({
    id: 0,
    name: "",
    organizations: [],
    status: UserStatus.PENDING,
    superadmin: false,
  }),
  syncEmailVerificationFromFirebase: async () => {},
  resendEmailVerification: async () => "sent",
  sendPasswordResetEmail: async () => {},
  setDeepLink: () => {},
  cancelDeepLinkNavigation: () => {},
  clearAuthError: () => {},
  completeMfaChallenge: async () => {},
  cancelMfaChallenge: () => {},
  getMfaEnrollmentFactors: async () => [],
  getReauthenticationProviderIds: () => [],
  reauthenticateForSensitiveOperation: async () => ({ mfaRequired: false }),
  completeSensitiveReauthenticationMfa: async () => {},
  beginTotpEnrollment: async () => {
    throw new Error("AuthProvider is not mounted");
  },
  completeTotpEnrollment: async () => {},
  removeMfaEnrollmentFactor: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
  auth: Auth;
  authenticateFirebaseUser: (user: User) => Promise<AuthResult>;
  googleClientId: string;
  /**
   * Send the stored deep link with the verification email, so the link lands back on that page.
   * The email always continues on app-site (`APP_BASE_URL` on the server), so only app-site may
   * opt in: another app's path, such as canvas-ui's `/canvas/123`, has no page there.
   */
  verificationEmailReturnsToDeepLink?: boolean;
}

export const AuthProvider = ({
  children,
  auth,
  authenticateFirebaseUser,
  googleClientId,
  verificationEmailReturnsToDeepLink = false,
}: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  const [navigatedToDeepLink, setNavigatedToDeepLink] = useState<string | null>(null);
  /**
   * The deep-link navigation has settled in the router. A loader redirect (e.g. "/" to the default
   * organization) never commits the parked URL itself, so exact-match arrival alone would wait
   * forever. `fromKey` is the location the navigation started from: the link is consumed only
   * once React has committed a different location, so the page that started the navigation (the
   * login page, with its own default redirect) is already gone when the link clears.
   */
  const [settledDeepLinkNavigation, setSettledDeepLinkNavigation] = useState<{
    target: string;
    fromKey: string;
  } | null>(null);
  /** Bumped on every auth-state event, so a navigation started for one session cannot settle into the next. */
  const authEventGenerationRef = useRef(0);
  const mfaResolverRef = useRef<MultiFactorResolver | null>(null);
  const sensitiveOperationMfaResolverRef = useRef<MultiFactorResolver | null>(null);
  const mfaChallengePendingRef = useRef(false);
  const mfaRetryPendingRef = useRef(false);
  const preserveDeepLinkOnSignOutRef = useRef<string | null>(null);
  const verificationReturnTo = useCallback(
    () => (verificationEmailReturnsToDeepLink ? toAuthReturnPath(getStoredDeepLink()) : undefined),
    [verificationEmailReturnsToDeepLink],
  );
  /**
   * Tracks whether we have seen a fully signed-in Firebase user in this tab. A transient user
   * emitted during MFA challenge setup must not count as a finished session, or the later
   * signed-out callback will wipe the parked deep link before the user can retry.
   */
  const hadFirebaseUserRef = useRef(false);
  const googleProvider = useMemo(() => {
    const provider = new GoogleAuthProvider();
    provider.addScope("https://www.googleapis.com/auth/userinfo.profile");
    provider.addScope("https://www.googleapis.com/auth/userinfo.email");
    provider.setCustomParameters({
      client_id: googleClientId,
    });
    return provider;
  }, [googleClientId]);

  const microsoftProvider = useMemo(() => {
    const provider = new OAuthProvider("microsoft.com");
    provider.setCustomParameters({
      prompt: "select_account",
    });
    provider.addScope("email");
    provider.addScope("openid");
    provider.addScope("profile");
    return provider;
  }, []);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const beginMfaChallenge = useCallback(
    (error: unknown): boolean => {
      try {
        const resolver = getMultiFactorResolver(auth, error as Parameters<typeof getMultiFactorResolver>[1]);
        mfaResolverRef.current = resolver;
        mfaChallengePendingRef.current = true;
        mfaRetryPendingRef.current = true;
        setAuthState((state) => ({
          ...state,
          ...pendingChallengeAuthPatch(),
        }));
        return true;
      } catch (resolverError) {
        console.error("Failed to start MFA challenge:", resolverError);
        mfaResolverRef.current = null;
        mfaRetryPendingRef.current = false;
        return false;
      }
    },
    [auth],
  );

  const clearMfaChallenge = useCallback(() => {
    mfaResolverRef.current = null;
    mfaChallengePendingRef.current = false;
    setAuthState((state) => ({
      ...state,
      ...cancelChallengePatch(),
    }));
  }, []);

  const failMfaChallengeToLoginForm = useCallback((kind: MfaChallengeErrorKind) => {
    mfaResolverRef.current = null;
    mfaChallengePendingRef.current = false;
    setAuthState((state) => ({
      ...state,
      ...expiredChallengeAuthPatch(kind),
    }));
  }, []);

  // Consume redirect result when returning from Google/Microsoft sign-in (required for signInWithRedirect flow)
  useEffect(() => {
    getRedirectResult(auth).catch((error) => {
      const classification = classifyRedirectFailure({
        error,
        mfaChallengePending: authState.mfaChallengePending,
        resolverCaptured: mfaResolverRef.current != null,
      });
      if (classification === "ignore") {
        return;
      }
      if (classification === "mfa") {
        if (beginMfaChallenge(error)) {
          return;
        }
        failMfaChallengeToLoginForm(mapMfaResolveError(error).kind);
        return;
      }
      console.error("Redirect sign-in error:", error);
      reportHandledError(error, { context: { flow: "redirect_sign_in" }, includeMessage: true });
      const message = formatRedirectFatalError(error);
      setAuthState((state) => ({
        ...state,
        loading: ApiLoadingState.error,
        isProcessingDeepLink: false,
        authError: message || "Sign-in failed",
        authErrorKind: null,
      }));
    });
  }, [auth, authState.mfaChallengePending, beginMfaChallenge, failMfaChallengeToLoginForm]);

  const setDeepLink = useCallback((url: string | null) => {
    setStoredDeepLink(url);
    setAuthState((state) => (state.deepLink === url ? state : { ...state, deepLink: url }));
  }, []);

  const cancelDeepLinkNavigation = useCallback(() => {
    setStoredDeepLink(null);
    setNavigatedToDeepLink(null);
    setSettledDeepLinkNavigation(null);
    setAuthState((state) =>
      state.deepLink === null && !state.isProcessingDeepLink
        ? state
        : {
            ...state,
            deepLink: null,
            isProcessingDeepLink: false,
          },
    );
  }, []);

  const clearAuthError = useCallback(() => {
    setAuthState((state) =>
      state.authError || state.authErrorKind ? { ...state, authError: null, authErrorKind: null } : state,
    );
  }, []);

  // Keep permissions.currentOrganizationId in sync with the module-level org id.
  // orgRouteLoader and useSwitchOrganization update the module store directly without
  // going through refreshSession, so without this the two sources can drift and policy
  // helpers (hasDashboardAccess, getManageAccessDeniedReason) can misread the current org.
  useEffect(() => {
    return addOrganizationListener((id) => {
      setAuthState((state) => {
        if (!state.permissions) return state;
        const newId = id ?? undefined;
        if (state.permissions.currentOrganizationId === newId) return state;
        return {
          ...state,
          permissions: { ...state.permissions, currentOrganizationId: newId },
        };
      });
    });
  }, []);

  /**
   * Refreshes the user's session by getting latest permissions from the backend
   */
  const refreshSession = useCallback(
    async (user: User): Promise<AuthenticatePermissions> => {
      try {
        const { permissions } = await authenticateFirebaseUser(user);
        const isEmailVerified = permissions.email_verified ?? user.emailVerified;

        if (permissions.status === UserStatus.PENDING) {
          mfaRetryPendingRef.current = false;
          setAuthState((state) => ({
            ...state,
            user,
            isEmailVerified,
            loading: ApiLoadingState.notLoading,
            permissions: {
              id: permissions.id,
              organizations: [],
              status: UserStatus.PENDING,
              currentOrganizationId: undefined,
              superadmin: permissions.superadmin,
              superadmin_mfa_required: permissions.superadmin_mfa_required,
            },
            isProcessingDeepLink: false,
            mfaChallengePending: false,
          }));
          mfaResolverRef.current = null;
          setCurrentOrganizationId(null);
          return permissions;
        }

        const currentOrgId = getCurrentOrganizationId();

        // Server returns organizations sorted with MRU first
        const firstUserOrgId = permissions.organizations.length > 0 ? permissions.organizations[0].id : undefined;

        // Validation logic
        const isCurrentOrgValid = currentOrgId !== null && permissions.organizations.some((o) => o.id === currentOrgId);

        const orgIdToSet = isCurrentOrgValid ? currentOrgId : firstUserOrgId;

        setCurrentOrganizationId(orgIdToSet ?? null);
        mfaRetryPendingRef.current = false;

        setAuthState((state) => ({
          ...state,
          user,
          isEmailVerified,
          loading: ApiLoadingState.notLoading,
          permissions: {
            id: permissions.id,
            organizations: permissions.organizations,
            currentOrganizationId: orgIdToSet,
            status: permissions.status,
            superadmin: permissions.superadmin,
            superadmin_mfa_required: permissions.superadmin_mfa_required,
          },
          isProcessingDeepLink: false,
          mfaChallengePending: false,
        }));
        mfaResolverRef.current = null;
        return permissions;
      } catch (error) {
        if (error instanceof ApiNotFoundError) {
          console.log("User not found in backend, signing out from Firebase and redirecting to login.");
          signOut(auth);
          navigate("/login?error=user_not_found");
        } else {
          setAuthState((state) => ({
            ...state,
            loading: ApiLoadingState.error,
            isProcessingDeepLink: false,
          }));
        }
        throw error;
      }
    },
    [authenticateFirebaseUser, navigate, auth],
  );

  const syncEmailVerificationFromFirebase = useCallback(async () => {
    const currentUser = authState.user;
    if (!currentUser) return;
    await reload(currentUser);
    if (currentUser.emailVerified) {
      await refreshSession(currentUser);
    }
  }, [authState.user, refreshSession]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: User | null) => {
      const generation = ++authEventGenerationRef.current;
      setSettledDeepLinkNavigation(null);
      const transition = classifyAuthStateTransition({
        hasUser: user !== null,
        sessionEndedForSignedInUser: hadFirebaseUserRef.current,
        mfaChallengePending: mfaChallengePendingRef.current,
        resolverCaptured: mfaResolverRef.current != null,
        mfaRetryPending: mfaRetryPendingRef.current,
      });
      if (transition === "authenticated" && user) {
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.loading,
        }));
        refreshSession(user)
          .then((permissions) => {
            if (permissions.status === UserStatus.PENDING || permissions.status === UserStatus.INACTIVE) {
              setDeepLink(null);
              return;
            }

            hadFirebaseUserRef.current = true;
            mfaChallengePendingRef.current = false;
            const currentDeepLink = getStoredDeepLink();
            if (currentDeepLink) {
              if (currentDeepLink === locationRef.current.pathname + locationRef.current.search) {
                setDeepLink(null);
                return;
              }
              setAuthState((state) => ({
                ...state,
                isProcessingDeepLink: true,
                deepLink: currentDeepLink,
              }));
              const fromKey = locationRef.current.key;
              setNavigatedToDeepLink(currentDeepLink);
              Promise.resolve(navigate(currentDeepLink))
                .then(() => {
                  if (authEventGenerationRef.current !== generation) return;
                  setSettledDeepLinkNavigation({ target: currentDeepLink, fromKey });
                })
                .catch((error: unknown) => {
                  // No message: it could carry the destination's query.
                  console.error("Deep-link navigation failed");
                  reportHandledError(error, { context: { flow: "deep_link_navigation" } });
                });
              return;
            }
          })
          .catch(console.error);
      } else {
        const preserveDeepLink = preserveDeepLinkOnSignOutRef.current;
        if (preserveDeepLink) {
          preserveDeepLinkOnSignOutRef.current = null;
          setStoredDeepLink(preserveDeepLink);
        }
        const clearDeepLinkForSessionEnd = transition === "signed_out_clear_deep_link" && preserveDeepLink === null;
        hadFirebaseUserRef.current = false;
        clearCurrentOrganization();
        if (clearDeepLinkForSessionEnd) {
          mfaRetryPendingRef.current = false;
          setStoredDeepLink(null);
          setNavigatedToDeepLink(null);
          mfaResolverRef.current = null;
        }
        setAuthState((state) => ({
          ...applySignedOutWhileChallengePending(state, clearDeepLinkForSessionEnd),
          deepLink: preserveDeepLink ?? (clearDeepLinkForSessionEnd ? null : state.deepLink),
        }));
      }
    });

    return () => unsubscribe();
  }, [navigate, refreshSession, auth, setDeepLink]);

  useEffect(() => {
    if (!navigatedToDeepLink) return;
    const arrived = hasReachedDeepLink(location.pathname, location.search, navigatedToDeepLink);
    const settledElsewhere =
      settledDeepLinkNavigation?.target === navigatedToDeepLink && settledDeepLinkNavigation.fromKey !== location.key;
    if (!arrived && !settledElsewhere) return;
    // A destination parked since this navigation started is a newer intent; leave it for its own sign-in.
    if (getStoredDeepLink() === navigatedToDeepLink) {
      setDeepLink(null);
    }
    setNavigatedToDeepLink(null);
    setSettledDeepLinkNavigation(null);
    setAuthState((state) => ({
      ...state,
      isProcessingDeepLink: false,
    }));
  }, [location, navigatedToDeepLink, settledDeepLinkNavigation, setDeepLink]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.loading,
          isProcessingDeepLink: true,
        }));
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error: unknown) {
        const interpretation = interpretSignInError(error);
        if (interpretation.action === "mfa_pending") {
          if (!beginMfaChallenge(error)) {
            failMfaChallengeToLoginForm(mapMfaResolveError(error).kind);
          }
          return;
        }
        console.error("Login error:", error);
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.error,
          isProcessingDeepLink: false,
        }));
        throw error;
      }
    },
    [auth, beginMfaChallenge, failMfaChallengeToLoginForm],
  );

  const signUp = useCallback(
    async (email: string, password: string) => {
      try {
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.loading,
          isProcessingDeepLink: true,
        }));
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const firebaseIdToken = await userCredential.user.getIdToken();
        await postRequestVerificationEmail(firebaseIdToken, verificationReturnTo());

        // The onAuthStateChanged listener will handle the rest
        return;
      } catch (error: any) {
        console.error("Sign up error:", error);
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.error,
          isProcessingDeepLink: false,
        }));
        throw error;
      }
    },
    [auth, verificationReturnTo],
  );

  /**
   * Sign in with an identity provider, by popup when the dev script asked for it (see
   * `shouldUseOAuthPopup`) and by redirect everywhere else.
   *
   * The two differ in where a failure surfaces. A redirect discards this page, so its errors — a
   * two-step-verification challenge included — arrive later in the `getRedirectResult` effect, and
   * throwing here only reports the failure to *start*. A popup resolves on a page that never
   * unloaded, so the same errors land in this catch instead and are handled here, using the same
   * classification the redirect effect uses. Nothing is rethrown on that path: the caller would
   * paint a generic "sign-in failed" banner over the code prompt we just opened.
   */
  const startOAuthSignIn = useCallback(
    async (provider: GoogleAuthProvider | OAuthProvider, label: string) => {
      setAuthState((state) => ({
        ...state,
        loading: ApiLoadingState.loading,
        isProcessingDeepLink: true,
      }));

      if (!shouldUseOAuthPopup()) {
        try {
          await signInWithRedirect(auth, provider);
        } catch (error: any) {
          console.error(`${label} login error:`, error);
          setAuthState((state) => ({
            ...state,
            loading: ApiLoadingState.error,
            isProcessingDeepLink: false,
          }));
          throw error;
        }
        return;
      }

      try {
        await signInWithPopup(auth, provider);
      } catch (error: unknown) {
        if (isPopupCancelledError(error)) {
          setAuthState((state) => ({
            ...state,
            loading: ApiLoadingState.notLoading,
            isProcessingDeepLink: false,
          }));
          return;
        }
        const classification = classifyRedirectFailure({
          error,
          mfaChallengePending: mfaChallengePendingRef.current,
          resolverCaptured: mfaResolverRef.current != null,
        });
        if (classification === "ignore") {
          return;
        }
        if (classification === "mfa") {
          if (beginMfaChallenge(error)) {
            return;
          }
          failMfaChallengeToLoginForm(mapMfaResolveError(error).kind);
          return;
        }
        console.error(`${label} login error:`, error);
        reportHandledError(error, { context: { flow: "popup_sign_in" }, includeMessage: true });
        setAuthState((state) => ({
          ...state,
          loading: ApiLoadingState.error,
          isProcessingDeepLink: false,
          authError: formatRedirectFatalError(error) || "Sign-in failed",
          authErrorKind: null,
        }));
      }
    },
    [auth, beginMfaChallenge, failMfaChallengeToLoginForm],
  );

  const loginWithGoogle = useCallback(async () => {
    await startOAuthSignIn(googleProvider, "Google");
  }, [googleProvider, startOAuthSignIn]);

  const loginWithMicrosoft = useCallback(async () => {
    await startOAuthSignIn(microsoftProvider, "Microsoft");
  }, [microsoftProvider, startOAuthSignIn]);

  const completeMfaChallenge = useCallback(
    async (otpCode: string) => {
      const resolver = mfaResolverRef.current;
      if (!resolver) {
        throw mapMfaResolveError(new Error("missing_resolver"));
      }
      const totpHint = selectTotpHint(resolver);
      if (!totpHint) {
        const mapped = mapMfaResolveError(new Error("missing_hint"));
        failMfaChallengeToLoginForm(mapped.kind);
        throw mapped;
      }
      try {
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, otpCode.trim());
        await resolver.resolveSignIn(assertion);
        mfaResolverRef.current = null;
        mfaChallengePendingRef.current = false;
        mfaRetryPendingRef.current = false;
        setAuthState((state) => ({
          ...state,
          ...resolvedChallengeAuthPatch(),
        }));
      } catch (error: unknown) {
        const mapped = mapMfaResolveError(error);
        if (mapped.kind === "expired") {
          failMfaChallengeToLoginForm(mapped.kind);
        }
        throw mapped;
      }
    },
    [failMfaChallengeToLoginForm],
  );

  const cancelMfaChallenge = useCallback(() => {
    clearMfaChallenge();
  }, [clearMfaChallenge]);

  const getCurrentUserForSensitiveOperation = useCallback((): User => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new MfaEnrollmentError("signed_out");
    }
    return currentUser;
  }, [auth]);

  const getMfaEnrollmentFactors = useCallback(async (): Promise<MfaEnrollmentFactor[]> => {
    const currentUser = getCurrentUserForSensitiveOperation();
    await reload(currentUser);
    return getTotpEnrollmentFactors(currentUser);
  }, [getCurrentUserForSensitiveOperation]);

  const getSensitiveOperationProviderIds = useCallback((): string[] => {
    const currentUser = auth.currentUser;
    return currentUser ? getReauthenticationProviderIds(currentUser) : [];
  }, [auth]);

  const reauthenticateForSensitiveOperation = useCallback(
    async (providerId: string, password?: string): Promise<{ mfaRequired: boolean }> => {
      const currentUser = getCurrentUserForSensitiveOperation();
      try {
        if (providerId === PASSWORD_PROVIDER_ID) {
          if (!currentUser.email || !password) {
            throw new MfaEnrollmentError("password_required");
          }
          await reauthenticateWithCredential(currentUser, EmailAuthProvider.credential(currentUser.email, password));
        } else if (providerId === "google.com") {
          await reauthenticateWithPopup(currentUser, googleProvider);
        } else if (providerId === "microsoft.com") {
          await reauthenticateWithPopup(currentUser, microsoftProvider);
        } else {
          throw new MfaEnrollmentError("unsupported_provider");
        }
        return { mfaRequired: false };
      } catch (error) {
        if (isMultiFactorAuthRequiredError(error)) {
          sensitiveOperationMfaResolverRef.current = getMultiFactorResolver(
            auth,
            error as Parameters<typeof getMultiFactorResolver>[1],
          );
          return { mfaRequired: true };
        }
        throw mapMfaEnrollmentError(error);
      }
    },
    [auth, getCurrentUserForSensitiveOperation, googleProvider, microsoftProvider],
  );

  const completeSensitiveReauthenticationMfa = useCallback(async (otpCode: string): Promise<void> => {
    const resolver = sensitiveOperationMfaResolverRef.current;
    if (!resolver) {
      throw mapMfaEnrollmentError(new Error("missing_sensitive_operation_mfa_resolver"));
    }
    const hint = selectTotpHint(resolver);
    if (!hint) {
      sensitiveOperationMfaResolverRef.current = null;
      throw mapMfaEnrollmentError(new Error("missing_sensitive_operation_mfa_hint"));
    }
    try {
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otpCode.trim());
      await resolver.resolveSignIn(assertion);
      sensitiveOperationMfaResolverRef.current = null;
    } catch (error) {
      throw mapMfaEnrollmentError(error);
    }
  }, []);

  const beginTotpEnrollment = useCallback(async (): Promise<TotpSecret> => {
    try {
      const currentUser = getCurrentUserForSensitiveOperation();
      const session = await multiFactor(currentUser).getSession();
      return await TotpMultiFactorGenerator.generateSecret(session);
    } catch (error) {
      throw mapMfaEnrollmentError(error);
    }
  }, [getCurrentUserForSensitiveOperation]);

  const completeTotpEnrollment = useCallback(
    async (secret: TotpSecret, otpCode: string): Promise<void> => {
      try {
        const currentUser = getCurrentUserForSensitiveOperation();
        const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, otpCode.trim());
        await multiFactor(currentUser).enroll(assertion, "Authenticator app");
      } catch (error) {
        throw mapMfaEnrollmentError(error);
      }
    },
    [getCurrentUserForSensitiveOperation],
  );

  const removeMfaEnrollmentFactor = useCallback(
    async (factorUid: string): Promise<void> => {
      try {
        const currentUser = getCurrentUserForSensitiveOperation();
        let permissions: AuthenticatePermissions;
        try {
          // Read fresh memberships without rehydrating AuthContext: the account may change
          // while this request is in flight, and a policy check must not restore the old user.
          ({ permissions } = await authenticateFirebaseUser(currentUser));
          await reload(currentUser);
        } catch (error) {
          // An expired or disabled session is not a policy answer, and Retry cannot fix it: report
          // it as itself. Only what the mapping does not recognise means the policy read failed.
          const mapped = mapMfaEnrollmentError(error);
          throw mapped.kind === "generic" ? new MfaEnrollmentError("policy_unavailable") : mapped;
        }
        if (auth.currentUser?.uid !== currentUser.uid) {
          throw new MfaEnrollmentError("signed_out");
        }
        const factors = multiFactor(currentUser);
        if (factors.enrolledFactors.length <= 1 && membershipsRequireMfa(permissions)) {
          throw new MfaEnrollmentError("policy_required");
        }
        await factors.unenroll(factorUid);
      } catch (error) {
        throw mapMfaEnrollmentError(error);
      }
    },
    [getCurrentUserForSensitiveOperation, authenticateFirebaseUser, auth],
  );

  const logout = useCallback(
    async (options?: { preserveDeepLink?: string }) => {
      try {
        const preserved = await signOutPreservingDeepLink({
          signOut: () => signOut(auth),
          preserveDeepLink: options?.preserveDeepLink,
          markPreserved: (url) => {
            preserveDeepLinkOnSignOutRef.current = url;
          },
        });
        clearCurrentOrganization();
        mfaResolverRef.current = null;
        sensitiveOperationMfaResolverRef.current = null;
        mfaChallengePendingRef.current = false;
        mfaRetryPendingRef.current = false;
        setAuthState({
          user: null,
          isEmailVerified: false,
          loading: ApiLoadingState.notLoading,
          permissions: null,
          isProcessingDeepLink: false,
          deepLink: preserved,
          authError: null,
          authErrorKind: null,
          mfaChallengePending: false,
        });
      } catch (error) {
        console.error("Logout error:", error);
      }
    },
    [auth],
  );

  const resendEmailVerification = useCallback(async (): Promise<"sent" | "alreadyVerified"> => {
    const currentUser = authState.user;
    if (!currentUser) return "alreadyVerified";
    await reload(currentUser);
    if (currentUser.emailVerified) {
      await refreshSession(currentUser);
      return "alreadyVerified";
    }
    const firebaseIdToken = await currentUser.getIdToken();
    await postRequestVerificationEmail(firebaseIdToken, verificationReturnTo());
    return "sent";
  }, [authState.user, refreshSession, verificationReturnTo]);

  const sendPasswordResetEmailToUser = useCallback(async (email: string) => {
    await postRequestPasswordReset(email);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        signUp,
        loginWithGoogle,
        loginWithMicrosoft,
        logout,
        refreshSession,
        syncEmailVerificationFromFirebase,
        resendEmailVerification,
        sendPasswordResetEmail: sendPasswordResetEmailToUser,
        setDeepLink,
        cancelDeepLinkNavigation,
        clearAuthError,
        completeMfaChallenge,
        cancelMfaChallenge,
        getMfaEnrollmentFactors,
        getReauthenticationProviderIds: getSensitiveOperationProviderIds,
        reauthenticateForSensitiveOperation,
        completeSensitiveReauthenticationMfa,
        beginTotpEnrollment,
        completeTotpEnrollment,
        removeMfaEnrollmentFactor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
